<template>
  <Layout>
    <PageHeader title="Badge Builder" :items="items" />
    <FeatureFieldAlert required-field="badge_name" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 class="card-title mb-0">Templates de Etiquetas</h4>
                <p class="text-muted mb-0">Crie e gerencie templates de badges</p>
              </div>
              <div class="d-flex">
                <router-link to="/badge-builder/settings" class="btn btn-outline-secondary mr-2">
                  <i class="bx bx-cog"></i> Configuracoes
                </router-link>
                <router-link to="/badge-builder/print-queue" class="btn btn-outline-primary mr-2">
                  <i class="bx bx-printer"></i> Fila de Impressao
                </router-link>
                <router-link to="/badge-builder/builder" class="btn btn-primary">
                  <i class="bx bx-plus"></i> Novo Template
                </router-link>
              </div>
            </div>

            <div class="mb-3">
              <input v-model="search" type="text" class="form-control" placeholder="Buscar templates..." />
            </div>

            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary"></b-spinner>
              <p class="text-muted mt-2">Carregando templates...</p>
            </div>

            <div v-else-if="filteredTemplates.length > 0" class="row">
              <div v-for="template in filteredTemplates" :key="template.id" class="col-md-4 col-lg-3">
                <div class="template-card">
                  <div class="template-preview">
                    <img
                      v-if="template.thumbnail_url"
                      :src="template.thumbnail_url"
                      :alt="template.name"
                      class="preview-thumbnail"
                    />
                    <div v-else class="preview-badge" :style="getPreviewStyle(template)">
                      <span class="preview-size">{{ template.width }}x{{ template.height }}mm</span>
                    </div>
                  </div>
                  <div class="template-info">
                    <h5 class="template-name">{{ template.name }}</h5>
                    <p class="template-meta">
                      <i class="bx bx-printer text-muted"></i> {{ template.printer_model }}
                    </p>
                    <p class="template-meta">
                      <i class="bx bx-ruler text-muted"></i> {{ template.width }}x{{ template.height }}mm
                      <span class="text-muted">·</span>
                      {{ template.orientation === 'portrait' ? 'Retrato' : 'Paisagem' }}
                    </p>
                    <span class="badge-placeholder">
                      <span v-if="template.is_default" class="badge badge-soft-primary">
                        Padrão
                      </span>
                    </span>
                  </div>
                  <div class="template-actions">
                    <button
                      :class="['btn', 'btn-sm', template.is_default ? 'btn-warning' : 'btn-light']"
                      :disabled="template.is_default"
                      @click="!template.is_default && setAsDefault(template)"
                      :title="template.is_default ? 'Template Padrão' : 'Definir como Padrão'"
                    >
                      <i class="bx bx-star"></i>
                    </button>
                    <button class="btn btn-sm btn-light" @click="duplicateTemplate(template.id)" title="Duplicar">
                      <i class="bx bx-duplicate"></i>
                    </button>
                    <router-link :to="getEditUrl(template.id)" class="btn btn-sm btn-primary" title="Editar">
                      <i class="bx bx-pencil"></i>
                    </router-link>
                    <button class="btn btn-sm btn-danger" @click="confirmDelete(template)" title="Deletar">
                      <i class="bx bx-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-5">
              <i class="bx bx-badge display-4 text-muted"></i>
              <h5 class="mt-3">Nenhum template encontrado</h5>
              <p class="text-muted">Crie seu primeiro template de etiqueta</p>
              <router-link to="/badge-builder/builder" class="btn btn-primary mt-2">
                <i class="bx bx-plus"></i> Criar Template
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal v-model="deleteModal.show" title="Confirmar Exclusao" ok-variant="danger" ok-title="Deletar" cancel-title="Cancelar" @ok="deleteTemplate">
      <p>Tem certeza que deseja deletar o template <strong>{{ deleteModal.template ? deleteModal.template.name : '' }}</strong>?</p>
      <p class="text-muted mb-0">Esta acao nao pode ser desfeita.</p>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import { successMessage, errorMessage } from '@/components/alertsMessages'
import FeatureFieldAlert from '@/components/FeatureFieldAlert'
import * as services from './services'

export default {
  name: 'BadgeBuilderIndex',
  components: {
    Layout,
    PageHeader,
    FeatureFieldAlert
  },
  data: function () {
    return {
      items: [
        { text: 'Eventfy', to: '/' },
        { text: 'Badge Builder', active: true }
      ],
      loading: false,
      search: '',
      templates: [],
      deleteModal: {
        show: false,
        template: null
      }
    }
  },
  computed: {
    filteredTemplates: function () {
      var self = this
      return this.templates.filter(function (template) {
        return !self.search || template.name.toLowerCase().indexOf(self.search.toLowerCase()) !== -1
      })
    }
  },
  mounted: function () {
    this.loadTemplates()
  },
  methods: {
    getPreviewStyle: function (template) {
      return {
        backgroundColor: template.background_color,
        aspectRatio: template.width + ' / ' + template.height
      }
    },
    getEditUrl: function (id) {
      return '/badge-builder/builder/' + id
    },
    loadTemplates: function () {
      var self = this
      self.loading = true
      services.listTemplates()
        .then(function (response) {
          self.templates = response.data.data
        })
        .catch(function (error) {
          console.error('Error loading templates:', error)
          errorMessage('Falha ao carregar templates')
        })
        .finally(function () {
          self.loading = false
        })
    },
    duplicateTemplate: function (id) {
      var self = this
      services.duplicateTemplate(id)
        .then(function () {
          successMessage('Template duplicado com sucesso')
          self.loadTemplates()
        })
        .catch(function (error) {
          console.error('Error duplicating template:', error)
          errorMessage('Falha ao duplicar template')
        })
    },
    confirmDelete: function (template) {
      this.deleteModal.template = template
      this.deleteModal.show = true
    },
    deleteTemplate: function () {
      var self = this
      services.deleteTemplate(self.deleteModal.template.id)
        .then(function () {
          successMessage('Template deletado com sucesso')
          self.loadTemplates()
        })
        .catch(function (error) {
          console.error('Error deleting template:', error)
          errorMessage(error.message ? error.message : 'Falha ao deletar template')
        })
    },
    setAsDefault: function (template) {
      var self = this
      services.setAsDefault(template.id)
        .then(function () {
          // Atualizar lista local
          self.templates.forEach(function (t) {
            t.is_default = t.id === template.id
          })
          successMessage('Template definido como padrão!')
        })
        .catch(function (error) {
          console.error('Error setting default:', error)
          errorMessage('Falha ao definir template como padrão')
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.template-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  margin-bottom: 1.5rem;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.template-preview {
  background: #f8f9fa;
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  height: 200px;
}

.preview-thumbnail {
  max-width: 100%;
  max-height: 170px;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-badge {
  position: relative;
  border: 2px dashed #dee2e6;
  border-radius: 4px;
  max-width: 100%;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.preview-size {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
}

.template-info {
  padding: 1rem;
}

.template-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.template-meta {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;

  i {
    margin-right: 0.25rem;
  }
}

.badge-placeholder {
  display: block;
  min-height: 22px;
}

.template-actions {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
</style>
